import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { receiptBankingService } from "services";
import {
  ReceiptBankingForm,
  ReceiptBankingQuery,
  ReceiptBankingUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type ReceiptBankingListKey =
  | ["receipt-bankings"]
  | ["receipt-bankings", ReceiptBankingQuery];
type ReceiptBankingDetailKey = ["receipt-bankings", "detail", number | string];

const receiptBankingKeys = {
  all: ["receipt-bankings"] as const,
  list: (params?: ReceiptBankingQuery): ReceiptBankingListKey =>
    params ? ["receipt-bankings", params] : ["receipt-bankings"],
  detail: (id: number | string): ReceiptBankingDetailKey => [
    "receipt-bankings",
    "detail",
    id,
  ],
};

export function useReceiptBanking() {
  const queryClient = useQueryClient();

  const fetchList = (params?: ReceiptBankingQuery, enabled = true) => {
    return useQuery({
      queryKey: receiptBankingKeys.list(params),
      queryFn: () => receiptBankingService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: receiptBankingKeys.detail(id),
      queryFn: () => receiptBankingService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createReceiptBanking = useMutation({
    mutationFn: (payload: ReceiptBankingForm) =>
      receiptBankingService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(
          res?.message ?? "Tạo phiếu thu ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: receiptBankingKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo phiếu thu ngân hàng thất bại");
      console.error("createReceiptBanking failed", err);
    },
  });

  const updateReceiptBanking = useMutation({
    mutationFn: (payload: ReceiptBankingUpdatePayload) =>
      receiptBankingService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Cập nhật phiếu thu ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: receiptBankingKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật phiếu thu ngân hàng thất bại");
      console.error("updateReceiptBanking failed", err);
    },
  });

  const deleteReceiptBanking = useMutation({
    mutationFn: (id: number | string) => receiptBankingService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Xoá phiếu thu ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: receiptBankingKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá phiếu thu ngân hàng thất bại");
      console.error("deleteReceiptBanking failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createReceiptBanking,
    updateReceiptBanking,
    deleteReceiptBanking,
  };
}
